import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

// ── Admin → PTO Tracker tab (Phase 1) ─────────────────────────────────────────
// Admin/Manager view of employee PTO: hire-date setup + backfill, status
// management, live balances, and manual adjustments. No employee-facing request
// flow yet (Phase 2). Balances are computed server-side by summing the ledger;
// this UI never stores or derives a balance itself.

const STATUSES = ['Active', 'Inactive', 'LOA'];

// Balances travel as signed half-days (1 day = 2 half-days).
function fmtBalance(halfDays) {
  if (halfDays == null) return '—';
  const days = halfDays / 2;
  return `${days} day${days === 1 ? '' : 's'}`;
}

function statusBadgeClass(status) {
  switch (status) {
    case 'Active':   return 'adm-badge adm-badge-shoot';
    case 'First90':  return 'adm-badge adm-badge-writer';
    case 'LOA':      return 'adm-badge adm-badge-post';
    case 'Inactive': return 'adm-badge adm-badge-admin';
    default:         return 'adm-badge';
  }
}

// ── Adjustment modal ──────────────────────────────────────────────────────────
// Two modes:
//   'adjust' — book a signed +/− delta (grant or deduct days).
//   'set'    — SET the available balance to an exact number; the server books
//              the correcting entry (target − current). Used for mid-year starts
//              where days have already been used and accrual doesn't reflect it.
function AdjustModal({ employee, onClose, onSaved }) {
  const [mode, setMode] = useState('adjust');
  const [days, setDays] = useState('');       // entered in DAYS, converted to half-days
  const [reason, setReason] = useState('');
  const [docReference, setDocReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    setErr('');
    const daysNum = Number(days);
    if (days === '' || Number.isNaN(daysNum)) {
      setErr(mode === 'set' ? 'Enter the balance in days.' : 'Enter a number of days.');
      return;
    }
    if (!Number.isInteger(daysNum * 2)) {
      setErr('Days must be in half-day increments (e.g. 1, 0.5' + (mode === 'set' ? ').' : ', -2).'));
      return;
    }
    if (mode === 'adjust' && daysNum === 0) {
      setErr('Enter a non-zero number of days (use negative to deduct).'); return;
    }
    if (mode === 'set' && daysNum < 0) {
      setErr('A balance can\'t be negative.'); return;
    }
    if (!reason.trim()) { setErr('A reason is required.'); return; }
    setSaving(true);
    try {
      if (mode === 'set') {
        await api.put(`/api/pto/employees/${employee.teamMemberId}/balance`, {
          targetHalfDays: Math.round(daysNum * 2),
          reason: reason.trim(),
          docReference: docReference.trim() || undefined,
        });
      } else {
        await api.post(`/api/pto/employees/${employee.teamMemberId}/adjustment`, {
          halfDays: Math.round(daysNum * 2),
          reason: reason.trim(),
          docReference: docReference.trim() || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const curDays = employee.balanceHalfDays == null ? null : employee.balanceHalfDays / 2;
  const targetNum = Number(days);
  const previewValid = mode === 'set' && days !== '' && !Number.isNaN(targetNum)
    && Number.isInteger(targetNum * 2) && targetNum >= 0 && curDays != null;
  const correction = previewValid ? targetNum - curDays : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 440 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">Admin / PTO Tracker</div>
            <div className="vc-title">{mode === 'set' ? 'Set PTO balance' : 'Adjust PTO'} — {employee.name}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div className="vc-body">
          {/* Mode toggle */}
          <div className="vc-field full" style={{ display: 'flex', gap: 6 }}>
            <button type="button"
                    className={mode === 'adjust' ? 'btn-primary' : 'btn-ghost'}
                    style={{ flex: 1, padding: '4px 8px', fontSize: 12 }}
                    onClick={() => { setMode('adjust'); setDays(''); setErr(''); }}>
              Adjust by (+/−)
            </button>
            <button type="button"
                    className={mode === 'set' ? 'btn-primary' : 'btn-ghost'}
                    style={{ flex: 1, padding: '4px 8px', fontSize: 12 }}
                    onClick={() => { setMode('set'); setDays(''); setErr(''); }}>
              Set to exact
            </button>
          </div>

          <div className="vc-field full" style={{ fontSize: 12, color: 'var(--tx-muted)' }}>
            Current balance: <strong>{fmtBalance(employee.balanceHalfDays)}</strong>
          </div>

          {mode === 'set' ? (
            <div className="vc-field full">
              <label>Set available balance to (days) *</label>
              <input className="vc-input" type="number" step="0.5" min="0" value={days}
                     onChange={e => setDays(e.target.value)} placeholder="e.g. 4.5" />
              {previewValid && (
                <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 6 }}>
                  {correction === 0
                    ? 'Already at this balance — no change will be booked.'
                    : <>System will book a <strong>{correction > 0 ? '+' : ''}{correction} day</strong> correction → new balance <strong>{targetNum} days</strong>.</>}
                </div>
              )}
            </div>
          ) : (
            <div className="vc-field full">
              <label>Days (+ grant / − deduct) *</label>
              <input className="vc-input" type="number" step="0.5" value={days}
                     onChange={e => setDays(e.target.value)} placeholder="e.g. 2 or -1.5" />
            </div>
          )}

          <div className="vc-field full">
            <label>Reason *</label>
            <input className="vc-input" type="text" value={reason} onChange={e => setReason(e.target.value)}
                   placeholder={mode === 'set' ? 'e.g. Mid-year start — 5.5 days already used' : 'Why is this adjustment being made?'} />
          </div>
          <div className="vc-field full">
            <label>Doc reference (optional)</label>
            <input className="vc-input" type="text" value={docReference} onChange={e => setDocReference(e.target.value)}
                   placeholder="Link / note if this differs from policy" />
          </div>
          {err && (
            <div className="vc-field full" style={{ color: 'var(--s-incomplete-t)', fontSize: 12 }}>{err}</div>
          )}
        </div>
        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'set' ? 'Set balance' : 'Record adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Employees view ────────────────────────────────────────────────────────────
function EmployeesView() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [editingHire, setEditingHire] = useState(null); // teamMemberId being edited
  const [hireDraft, setHireDraft] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await api.get('/api/pto/employees');
      setEmployees(data.employees || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load PTO employees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveHireDate = async (emp) => {
    if (!hireDraft || !/^\d{4}-\d{2}-\d{2}$/.test(hireDraft)) {
      setErr('Enter a valid hire date (YYYY-MM-DD).');
      return;
    }
    setBusyId(emp.teamMemberId);
    setErr('');
    try {
      await api.put(`/api/pto/employees/${emp.teamMemberId}/hire-date`, { hireDate: hireDraft });
      setEditingHire(null);
      setHireDraft('');
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to set hire date.');
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = async (emp, status) => {
    if (status === emp.status) return;
    if (status === 'Inactive' &&
        !window.confirm(`Mark ${emp.name} Inactive? This forfeits their current-year balance (no payout).`)) {
      return;
    }
    setBusyId(emp.teamMemberId);
    setErr('');
    try {
      await api.put(`/api/pto/employees/${emp.teamMemberId}/status`, { status });
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to change status.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--tx-muted)' }}>
          Set a hire date to enroll an employee. Balances accrue automatically at the 90-day mark
          and reset on each work anniversary.
        </div>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table className="adm-table">
          <thead>
            <tr>
              <th className="adm-th">Name</th>
              <th className="adm-th">Hire Date</th>
              <th className="adm-th">Status</th>
              <th className="adm-th">Balance</th>
              <th className="adm-th" style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && !loading && (
              <tr>
                <td className="adm-td" colSpan={5}
                    style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: '32px 16px' }}>
                  No internal staff found.
                </td>
              </tr>
            )}
            {employees.map(emp => {
              const isEditing = editingHire === emp.teamMemberId;
              const busy = busyId === emp.teamMemberId;
              return (
                <tr key={emp.teamMemberId}>
                  <td className="adm-td primary">
                    {emp.name}
                    <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{emp.email}</div>
                  </td>
                  <td className="adm-td">
                    {isEditing ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <input type="date" value={hireDraft}
                               onChange={e => setHireDraft(e.target.value)}
                               style={{ fontSize: 12 }} />
                        <button className="btn-primary" style={{ padding: '2px 8px' }}
                                disabled={busy} onClick={() => saveHireDate(emp)}>Save</button>
                        <button className="btn-ghost" style={{ padding: '2px 8px' }}
                                onClick={() => { setEditingHire(null); setHireDraft(''); }}>Cancel</button>
                      </span>
                    ) : emp.hireDate ? (
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        {emp.hireDate}
                        <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}
                                onClick={() => { setEditingHire(emp.teamMemberId); setHireDraft(emp.hireDate); }}>
                          Edit
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <span className="adm-badge adm-badge-admin">⚠ Hire date needed</span>
                        <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}
                                onClick={() => { setEditingHire(emp.teamMemberId); setHireDraft(''); }}>
                          Set
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="adm-td">
                    {emp.status ? (
                      <select value={STATUSES.includes(emp.status) ? emp.status : ''}
                              disabled={busy || !emp.hireDate}
                              onChange={e => changeStatus(emp, e.target.value)}
                              style={{ fontSize: 12 }}>
                        {!STATUSES.includes(emp.status) && (
                          <option value="">{emp.status}</option>
                        )}
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className={statusBadgeClass(null)} style={{ color: 'var(--tx-faint)' }}>—</span>
                    )}
                    {emp.status === 'First90' && (
                      <span className={statusBadgeClass('First90')} style={{ marginLeft: 6 }}>First 90</span>
                    )}
                  </td>
                  <td className="adm-td">{emp.hireDate ? fmtBalance(emp.balanceHalfDays) : '—'}</td>
                  <td className="adm-td">
                    <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}
                            disabled={!emp.hireDate || busy}
                            onClick={() => setAdjustFor(emp)}>
                      Adjust
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {adjustFor && (
        <AdjustModal employee={adjustFor} onClose={() => setAdjustFor(null)} onSaved={load} />
      )}
    </div>
  );
}

// ── Approval Queue view ───────────────────────────────────────────────────────
function ApprovalQueue() {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await api.get(`/api/pto/requests${q}`);
      setRequests(data.requests || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load requests.');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusyId(r.id); setErr('');
    try { await api.post(`/api/pto/requests/${r.id}/approve`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to approve.'); }
    finally { setBusyId(null); }
  };
  const deny = async (r) => {
    const reason = window.prompt(`Deny ${r.memberName || 'this'} request — reason (required):`);
    if (reason == null) return;
    if (!reason.trim()) { setErr('A denial reason is required.'); return; }
    setBusyId(r.id); setErr('');
    try { await api.post(`/api/pto/requests/${r.id}/deny`, { denialReason: reason.trim() }); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to deny.'); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 13 }}>Status:&nbsp;
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {['Pending', 'Approved', 'Denied', 'Cancelled', 'Taken', ''].map(s =>
              <option key={s || 'all'} value={s}>{s || 'All'}</option>)}
          </select>
        </label>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="adm-table">
          <thead>
            <tr>
              <th className="adm-th">Employee</th>
              <th className="adm-th">Dates</th>
              <th className="adm-th">Days</th>
              <th className="adm-th">Reason</th>
              <th className="adm-th">Status</th>
              <th className="adm-th" style={{ width: 160 }}></th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && !loading && (
              <tr><td className="adm-td" colSpan={6} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: 24 }}>No requests.</td></tr>
            )}
            {requests.map(r => (
              <tr key={r.id}>
                <td className="adm-td primary">{r.memberName || '—'}
                  <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{r.memberEmail}</div>
                </td>
                <td className="adm-td">
                  {r.startDate} → {r.endDate}{(r.halfStart || r.halfEnd) ? ' ½' : ''}
                  {r.pastDated && <span className="adm-badge adm-badge-admin" style={{ marginLeft: 6 }}>past-dated</span>}
                </td>
                <td className="adm-td">{fmtBalance(r.totalHalfDays)}</td>
                <td className="adm-td">{r.reason}{r.note ? <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{r.note}</div> : null}</td>
                <td className="adm-td">
                  <span className={statusBadgeClass(r.status === 'Approved' ? 'Active' : null)}>{r.status}</span>
                  {r.status === 'Denied' && r.denialReason && <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{r.denialReason}</div>}
                </td>
                <td className="adm-td">
                  {r.status === 'Pending' && (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button className="btn-primary" style={{ padding: '2px 10px' }} disabled={busyId === r.id} onClick={() => approve(r)}>Approve</button>
                      <button className="btn-ghost" style={{ padding: '2px 10px' }} disabled={busyId === r.id} onClick={() => deny(r)}>Deny</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Holiday Calendar view ─────────────────────────────────────────────────────
function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ holidayDate: '', name: '' });

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const { data } = await api.get('/api/pto/holidays'); setHolidays(data.holidays || []); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to load holidays.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.holidayDate || !form.name.trim()) { setErr('Date and name are required.'); return; }
    setErr('');
    try { await api.post('/api/pto/holidays', { holidayDate: form.holidayDate, name: form.name.trim() }); setForm({ holidayDate: '', name: '' }); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to add holiday.'); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Remove holiday "${h.name}" (${h.holidayDate})?`)) return;
    try { await api.delete(`/api/pto/holidays/${h.id}`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to remove holiday.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <label className="vc-field"><span>Date</span>
          <input className="vc-input" type="date" value={form.holidayDate} onChange={e => setForm(f => ({ ...f, holidayDate: e.target.value }))} />
        </label>
        <label className="vc-field" style={{ minWidth: 220 }}><span>Name</span>
          <input className="vc-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Thanksgiving" />
        </label>
        <button className="btn-primary" onClick={add}>+ Add holiday</button>
        <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="adm-table">
          <thead><tr><th className="adm-th">Date</th><th className="adm-th">Name</th><th className="adm-th" style={{ width: 80 }}></th></tr></thead>
          <tbody>
            {holidays.length === 0 && !loading && (
              <tr><td className="adm-td" colSpan={3} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: 24 }}>No company holidays yet.</td></tr>
            )}
            {holidays.map(h => (
              <tr key={h.id}>
                <td className="adm-td">{h.holidayDate}</td>
                <td className="adm-td">{h.name}</td>
                <td className="adm-td"><button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => remove(h)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Month calendar ("who's out") view ────────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const REQ_COLOR = { Approved: '#3b9c6d', Taken: '#8b6dff', Pending: '#c08a2d' };

function ymdUTC(d) { return d.toISOString().slice(0, 10); }
function addUTCDays(d, n) { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; }

function CalendarView() {
  // Cursor: first of the displayed month (UTC).
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
  const [data, setData] = useState({ requests: [], holidays: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // 6-week grid starting on the Sunday on/before the 1st.
  const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
  const gridStart = addUTCDays(first, -first.getUTCDay());
  const cells = Array.from({ length: 42 }, (_, i) => addUTCDays(gridStart, i));

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const start = ymdUTC(cells[0]);
      const end = ymdUTC(cells[41]);
      const { data } = await api.get(`/api/pto/calendar?start=${start}&end=${end}`);
      setData({ requests: data.requests || [], holidays: data.holidays || [] });
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load calendar.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor.year, cursor.month]);

  useEffect(() => { load(); }, [load]);

  const holidayByDate = {};
  data.holidays.forEach(h => { holidayByDate[h.holidayDate] = h.name; });

  // Which people are off on a given weekday (weekends + holidays excluded, to
  // match how PTO is actually counted).
  function entriesFor(dstr, isWeekend) {
    if (isWeekend || holidayByDate[dstr]) return [];
    return data.requests
      .filter(r => dstr >= r.startDate && dstr <= r.endDate)
      .map(r => ({
        ...r,
        half: (dstr === r.startDate && r.halfStart) || (dstr === r.endDate && r.halfEnd),
      }));
  }

  const step = (delta) => setCursor(c => {
    const m = c.month + delta;
    return { year: c.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
  });
  const goToday = () => setCursor({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
  const todayStr = ymdUTC(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button className="btn-ghost" onClick={() => step(-1)}>‹ Prev</button>
        <div style={{ fontWeight: 700, fontSize: 15, minWidth: 170, textAlign: 'center' }}>
          {MONTHS[cursor.month]} {cursor.year}
        </div>
        <button className="btn-ghost" onClick={() => step(1)}>Next ›</button>
        <button className="btn-ghost" onClick={goToday}>Today</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, color: 'var(--tx-muted)' }}>
          {Object.entries(REQ_COLOR).map(([k, c]) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />{k}
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--bg-hover)', border: '1px solid var(--border-mid)', display: 'inline-block' }} />Holiday
          </span>
          <button className="btn-ghost" onClick={load} disabled={loading}>{loading ? '…' : '↻'}</button>
        </div>
      </div>

      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        {DOW.map(d => (
          <div key={d} style={{ background: 'var(--bg-card)', padding: '6px 8px', fontSize: 11, fontWeight: 700, color: 'var(--tx-muted)', textAlign: 'center' }}>{d}</div>
        ))}
        {cells.map((cell, i) => {
          const dstr = ymdUTC(cell);
          const inMonth = cell.getUTCMonth() === cursor.month;
          const dow = cell.getUTCDay();
          const isWeekend = dow === 0 || dow === 6;
          const holiday = holidayByDate[dstr];
          const entries = entriesFor(dstr, isWeekend);
          const isToday = dstr === todayStr;
          return (
            <div key={i} style={{
              background: inMonth ? 'var(--bg-card)' : 'var(--bg-input)',
              minHeight: 96, padding: 6, display: 'flex', flexDirection: 'column', gap: 3,
              opacity: inMonth ? 1 : 0.55,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                  fontSize: 11, fontWeight: isToday ? 700 : 500,
                  color: isToday ? '#fff' : 'var(--tx-secondary)',
                  background: isToday ? 'var(--violet)' : 'transparent',
                  borderRadius: 10, padding: isToday ? '1px 6px' : 0, minWidth: 18, textAlign: 'center',
                }}>{cell.getUTCDate()}</span>
                {holiday && (
                  <span title={holiday} style={{ fontSize: 9, color: 'var(--tx-faint)', background: 'var(--bg-hover)', border: '1px solid var(--border-mid)', borderRadius: 3, padding: '0 4px', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{holiday}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
                {entries.map(e => (
                  <span key={e.id} title={`${e.memberName} — ${e.reason} (${e.status})${e.half ? ' · half day' : ''}`}
                    style={{
                      fontSize: 10, lineHeight: 1.3, padding: '1px 4px', borderRadius: 3,
                      borderLeft: `3px solid ${REQ_COLOR[e.status] || 'var(--border-mid)'}`,
                      background: 'var(--bg-hover)', color: 'var(--tx-primary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      opacity: e.status === 'Pending' ? 0.75 : 1,
                    }}>
                    {e.memberName.split(' ')[0]}{e.half ? ' ½' : ''}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {!loading && data.requests.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginTop: 12 }}>No time off scheduled this month.</div>
      )}
    </div>
  );
}

// ── Change Requests view (B3) ─────────────────────────────────────────────────
function ChangeRequestsView() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await api.get(`/api/pto/change-requests${q}`);
      setItems(data.changeRequests || []);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to load change requests.'); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (c) => {
    const daysStr = window.prompt(`Approve change for ${c.memberName}.\nEnter the balance adjustment in DAYS (+ credit / − deduct, half-day increments):`);
    if (daysStr == null) return;
    const daysNum = Number(daysStr);
    if (!daysStr || Number.isNaN(daysNum) || daysNum === 0 || !Number.isInteger(daysNum * 2)) {
      setErr('Adjustment must be a non-zero number of days in half-day increments.'); return;
    }
    const reason = window.prompt('Reason for this adjustment (required):');
    if (reason == null) return;
    if (!reason.trim()) { setErr('A reason is required.'); return; }
    setBusyId(c.id); setErr('');
    try {
      await api.post(`/api/pto/change-requests/${c.id}/resolve`, {
        decision: 'Approved', halfDays: Math.round(daysNum * 2), reason: reason.trim(),
      });
      await load();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to approve.'); }
    finally { setBusyId(null); }
  };
  const deny = async (c) => {
    const note = window.prompt('Deny — resolution note (required):');
    if (note == null) return;
    if (!note.trim()) { setErr('A resolution note is required.'); return; }
    setBusyId(c.id); setErr('');
    try {
      await api.post(`/api/pto/change-requests/${c.id}/resolve`, { decision: 'Denied', resolutionNote: note.trim() });
      await load();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to deny.'); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 13 }}>Status:&nbsp;
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {['Pending', 'Approved', 'Denied', ''].map(s => <option key={s || 'all'} value={s}>{s || 'All'}</option>)}
          </select>
        </label>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="adm-table">
          <thead><tr>
            <th className="adm-th">Employee</th><th className="adm-th">Original PTO</th>
            <th className="adm-th">Requested change</th><th className="adm-th">Status</th>
            <th className="adm-th" style={{ width: 160 }}></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td className="adm-td" colSpan={5} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: 24 }}>No change requests.</td></tr>
            )}
            {items.map(c => (
              <tr key={c.id}>
                <td className="adm-td primary">{c.memberName}<div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{c.memberEmail}</div></td>
                <td className="adm-td">{c.request ? `${c.request.startDate} → ${c.request.endDate} (${fmtBalance(c.request.totalHalfDays)}, ${c.request.status})` : '—'}</td>
                <td className="adm-td">{c.description}<div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{c.reason}</div></td>
                <td className="adm-td">
                  <span className={statusBadgeClass(c.status === 'Approved' ? 'Active' : c.status === 'Denied' ? 'Inactive' : 'First90')}>{c.status}</span>
                  {c.resolutionNote && <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{c.resolutionNote}</div>}
                </td>
                <td className="adm-td">
                  {c.status === 'Pending' && (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button className="btn-primary" style={{ padding: '2px 10px' }} disabled={busyId === c.id} onClick={() => approve(c)}>Approve</button>
                      <button className="btn-ghost" style={{ padding: '2px 10px' }} disabled={busyId === c.id} onClick={() => deny(c)}>Deny</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Reports view (B2) ─────────────────────────────────────────────────────────
function ReportsView() {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [reqFilters, setReqFilters] = useState({ from: '', to: '', status: '' });

  const download = async (path, filename, key) => {
    setBusy(key); setErr('');
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setErr('Download failed.'); }
    finally { setBusy(''); }
  };

  const reqQuery = () => {
    const p = new URLSearchParams();
    if (reqFilters.from) p.set('from', reqFilters.from);
    if (reqFilters.to) p.set('to', reqFilters.to);
    if (reqFilters.status) p.set('status', reqFilters.status);
    const s = p.toString();
    return `/api/pto/reports/requests.csv${s ? '?' + s : ''}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {err && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13 }}>{err}</div>}

      <div className="editor-card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Balances</div>
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 12 }}>Every enrolled member — hire date, status, benefit year, and all balance buckets (in days).</div>
        <button className="btn-primary" disabled={busy === 'bal'} onClick={() => download('/api/pto/reports/balances.csv', 'pto-balances.csv', 'bal')}>
          {busy === 'bal' ? 'Preparing…' : 'Download balances.csv'}
        </button>
      </div>

      <div className="editor-card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Requests</div>
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 12 }}>The request log, optionally filtered by start-date range and status.</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <label className="vc-field"><span>From</span><input className="vc-input" type="date" value={reqFilters.from} onChange={e => setReqFilters(f => ({ ...f, from: e.target.value }))} /></label>
          <label className="vc-field"><span>To</span><input className="vc-input" type="date" value={reqFilters.to} onChange={e => setReqFilters(f => ({ ...f, to: e.target.value }))} /></label>
          <label className="vc-field"><span>Status</span>
            <select className="vc-input" value={reqFilters.status} onChange={e => setReqFilters(f => ({ ...f, status: e.target.value }))}>
              {['', 'Pending', 'Approved', 'Denied', 'Cancelled', 'Taken'].map(s => <option key={s || 'all'} value={s}>{s || 'All'}</option>)}
            </select>
          </label>
        </div>
        <button className="btn-primary" disabled={busy === 'req'} onClick={() => download(reqQuery(), 'pto-requests.csv', 'req')}>
          {busy === 'req' ? 'Preparing…' : 'Download requests.csv'}
        </button>
      </div>

      <div className="editor-card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Expiring balances</div>
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 12 }}>Active members with unused PTO, soonest expiry first.</div>
        <button className="btn-primary" disabled={busy === 'exp'} onClick={() => download('/api/pto/reports/expiring.csv', 'pto-expiring.csv', 'exp')}>
          {busy === 'exp' ? 'Preparing…' : 'Download expiring.csv'}
        </button>
      </div>
    </div>
  );
}

// ── Main tab (view switcher) ──────────────────────────────────────────────────
const VIEWS = [
  { key: 'employees', label: 'Employees' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'approvals', label: 'Approval Queue' },
  { key: 'changes', label: 'Change Requests' },
  { key: 'holidays', label: 'Holiday Calendar' },
  { key: 'reports', label: 'Reports' },
];

export default function PtoTracker() {
  const [view, setView] = useState('employees');
  return (
    <div>
      <div className="adm-tabs" style={{ marginBottom: 16 }}>
        {VIEWS.map(v => (
          <button key={v.key} className={`adm-tab ${view === v.key ? 'active' : ''}`} onClick={() => setView(v.key)}>
            {v.label}
          </button>
        ))}
      </div>
      {view === 'employees' && <EmployeesView />}
      {view === 'calendar' && <CalendarView />}
      {view === 'approvals' && <ApprovalQueue />}
      {view === 'changes' && <ChangeRequestsView />}
      {view === 'holidays' && <HolidayCalendar />}
      {view === 'reports' && <ReportsView />}
    </div>
  );
}
